import * as React from "react";

import { Legend, TrendChart, asPct } from "@/components/evaluations/charts";
import { Tile, dayLabel, filterSelect } from "@/components/evaluations/parts";
import { Card, TBody, TD, TH, THead, TR, Table } from "@/components/ui/primitives";
import type { AccuracyKpis, AgentEvaluation } from "@/types/api";

// Accuracy on an agent's Evaluations page (AVI-172): the automatic score beside
// the evaluators' view of the same answers, how often they agree, and how each
// agent version compares.

const SERIES = [
  { name: "Automated (% accurate)", color: "var(--series-1)" },
  { name: "Evaluators (% 👍)", color: "var(--series-2)" },
];

const levels = (k: AccuracyKpis) => `High ${k.high} · Medium ${k.medium} · Low ${k.low}`;

export function AccuracySection({
  evaluation: a,
  days,
}: {
  evaluation: AgentEvaluation;
  days: number;
}) {
  const [version, setVersion] = React.useState("");
  const picked = a.accuracy_versions.find((v) => v.version === version);
  const trend = picked ? picked.trend : a.accuracy_trend;
  const k = a.accuracy;

  if (!k.scored && !k.human_rated) {
    return (
      <Card className="grid gap-1 p-4">
        <h2 className="text-base font-semibold">Accuracy</h2>
        <p className="text-sm text-muted-foreground">
          No scored or evaluator-rated answers in the last {days} days. Org admins can turn on
          automatic scoring at the bottom of the Evaluations page.
        </p>
      </Card>
    );
  }

  return (
    <div className="grid gap-3">
      <h2 className="text-base font-semibold">Accuracy</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile
          label={`Automated accuracy · ${days} days`}
          value={asPct(k.average_accuracy)}
          sub={k.scored ? `${levels(k)} of ${k.scored} scored` : "No answers scored"}
        />
        <Tile
          label="Evaluator-rated accuracy"
          value={asPct(k.human_rate)}
          sub={
            k.human_rated
              ? `👍 share of ${k.human_rated} evaluator rating${k.human_rated === 1 ? "" : "s"}`
              : "No evaluator ratings"
          }
        />
        <Tile
          label="Agreement"
          value={asPct(k.agreement_rate)}
          sub={
            k.compared
              ? `on ${k.compared} answer${k.compared === 1 ? "" : "s"} both scored and rated`
              : "No answer both scored and rated yet"
          }
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <Card className="grid gap-2 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">Accuracy over time</h3>
            <select
              aria-label="Version"
              className={filterSelect}
              value={version}
              onChange={(e) => setVersion(e.target.value)}
            >
              <option value="">All versions</option>
              {a.accuracy_versions.map((v) => (
                <option key={v.version} value={v.version}>
                  v{v.version}
                  {v.live ? " · live" : ""}
                </option>
              ))}
            </select>
          </div>
          <Legend series={SERIES} />
          <TrendChart
            labels={trend.map((p) => dayLabel(p.date))}
            series={[
              { ...SERIES[0]!, data: trend.map((p) => p.automated) },
              { ...SERIES[1]!, data: trend.map((p) => p.evaluators) },
            ]}
          />
        </Card>

        <Card className="grid content-start gap-3 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold">Accuracy by version</h3>
            <span className="text-xs text-muted-foreground">compare before promoting</span>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <TR>
                  <TH>Version</TH>
                  <TH className="text-right">Automated</TH>
                  <TH className="text-right">Evaluators</TH>
                  <TH className="text-right">Agreement</TH>
                </TR>
              </THead>
              <TBody>
                {a.accuracy_versions.map((v) => (
                  <TR key={v.version}>
                    <TD className="whitespace-nowrap font-mono text-xs">
                      v{v.version}
                      {v.live ? <span className="ml-1 font-sans text-primary">live</span> : null}
                    </TD>
                    <TD
                      className="text-right tabular-nums"
                      title={v.scored ? levels(v) : undefined}
                    >
                      {asPct(v.average_accuracy)}
                      <span className="block text-[11px] text-muted-foreground">
                        {v.scored} scored
                      </span>
                    </TD>
                    <TD className="text-right tabular-nums">
                      {asPct(v.human_rate)}
                      <span className="block text-[11px] text-muted-foreground">
                        {v.human_rated} rated
                      </span>
                    </TD>
                    <TD className="text-right tabular-nums">
                      {asPct(v.agreement_rate)}
                      <span className="block text-[11px] text-muted-foreground">
                        {v.compared} compared
                      </span>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground">
            Automated = average % accurate from the judge, over answers built on tool or
            knowledge-base data. Agreement: a 👍 agrees with High, a 👎 with Medium or Low.
          </p>
        </Card>
      </div>
    </div>
  );
}
